import { useState, useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import {
  ChevronUp,
  ChevronDown,
  Pencil,
  Trash2,
  Plus,
  Calendar,
  Star,
  ListChecks,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { AddEventDialog } from '@/components/Calendars/AddEventDialog'
import { EditEventDialog } from '@/components/Calendars/EditEventDialog'
import {
  useReorderCalendarEvents,
  useDeleteCalendarEvent,
} from '@/hooks/useCalendarEvents'
import { resolveAllDates } from '@/utils/calendarDateResolver'
import type { CalendarEventRead } from '@/types'

interface EventsListProps {
  events: CalendarEventRead[]
  calendarId: string
  onEventClick: (eventId: string) => void
}

function formatDateRange(
  event: CalendarEventRead,
  resolvedDates: Map<string, string | null>
): string | null {
  const dates = event.milestones
    .map(m => resolvedDates.get(m.id))
    .filter((d): d is string => d != null)
    .sort()

  if (dates.length === 0) return null

  const first = format(parseISO(dates[0]), 'MMM d, yyyy')
  const last = format(parseISO(dates[dates.length - 1]), 'MMM d, yyyy')

  return first === last ? first : `${first} - ${last}`
}

export function EventsList({
  events,
  calendarId,
  onEventClick,
}: EventsListProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editEvent, setEditEvent] = useState<CalendarEventRead | null>(null)
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null)

  const reorderEvents = useReorderCalendarEvents(calendarId)
  const deleteEvent = useDeleteCalendarEvent(calendarId)

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.display_order - b.display_order),
    [events]
  )

  const allMilestones = useMemo(
    () => events.flatMap(e => e.milestones),
    [events]
  )

  const resolvedDates = useMemo(
    () => resolveAllDates(allMilestones),
    [allMilestones]
  )

  const handleReorder = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...sortedEvents]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    if (swapIndex < 0 || swapIndex >= newOrder.length) return
    ;[newOrder[index], newOrder[swapIndex]] = [
      newOrder[swapIndex],
      newOrder[index],
    ]
    reorderEvents.mutate({ ordered_ids: newOrder.map(e => e.id) })
  }

  const handleDelete = () => {
    if (!deleteEventId) return
    deleteEvent.mutate(deleteEventId, {
      onSuccess: () => setDeleteEventId(null),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Events</h2>
        <Button
          size="sm"
          onClick={() => setAddDialogOpen(true)}
          className="cursor-pointer gap-1.5"
        >
          <Plus className="h-4 w-4" />
          Add Event
        </Button>
      </div>

      {sortedEvents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Calendar className="mb-2 h-8 w-8" />
            <p className="text-sm">No events yet. Add your first event.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {sortedEvents.map((event, index) => {
            const dateRange = formatDateRange(event, resolvedDates)
            return (
              <Card
                key={event.id}
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={() => onEventClick(event.id)}
              >
                <CardContent className="flex items-center gap-4 p-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold">
                    {event.display_order}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium">{event.name}</p>
                      <Badge variant="secondary">
                        {event.milestones.length}{' '}
                        {event.milestones.length === 1
                          ? 'milestone'
                          : 'milestones'}
                      </Badge>
                    </div>
                    {event.description && (
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">
                        {event.description}
                      </p>
                    )}
                    <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                      {event.owner && (
                        <span>
                          Owner:{' '}
                          {event.owner?.display_name ??
                            event.owner?.email ??
                            'Unknown'}
                        </span>
                      )}
                      {dateRange && <span>{dateRange}</span>}
                    </div>
                    {event.milestones.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {[...event.milestones]
                          .sort((a, b) => a.display_order - b.display_order)
                          .map(ms => {
                            const resolved = resolvedDates.get(ms.id)
                            const dateLabel = resolved
                              ? format(parseISO(resolved), 'MMM d')
                              : '—'
                            return (
                              <span
                                key={ms.id}
                                className={
                                  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] leading-tight ' +
                                  (ms.date_mode === 'CALCULATED'
                                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 bg-gray-50 text-gray-700')
                                }
                              >
                                {ms.milestone_type === 'STANDARDIZED' && (
                                  <Star className="h-2.5 w-2.5 fill-current" />
                                )}
                                <span className="font-medium">{ms.name}</span>
                                <span className="text-[10px] opacity-70">
                                  {dateLabel}
                                </span>
                              </span>
                            )
                          })}
                      </div>
                    )}
                  </div>

                  <div
                    className="flex items-center gap-1"
                    onClick={e => e.stopPropagation()}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 cursor-pointer gap-1.5 text-xs"
                      onClick={() => onEventClick(event.id)}
                    >
                      <ListChecks className="h-3.5 w-3.5" />
                      Milestones
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 cursor-pointer"
                      disabled={index === 0 || reorderEvents.isPending}
                      onClick={() => handleReorder(index, 'up')}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 cursor-pointer"
                      disabled={
                        index === sortedEvents.length - 1 ||
                        reorderEvents.isPending
                      }
                      onClick={() => handleReorder(index, 'down')}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 cursor-pointer"
                      onClick={() => setEditEvent(event)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 cursor-pointer text-destructive hover:text-destructive"
                      onClick={() => setDeleteEventId(event.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <AddEventDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        calendarId={calendarId}
      />

      {editEvent && (
        <EditEventDialog
          open={!!editEvent}
          onOpenChange={open => {
            if (!open) setEditEvent(null)
          }}
          calendarId={calendarId}
          event={editEvent}
        />
      )}

      <ConfirmationDialog
        open={!!deleteEventId}
        onOpenChange={open => {
          if (!open) setDeleteEventId(null)
        }}
        title="Delete Event"
        description="Are you sure you want to delete this event? All milestones within it will also be deleted. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteEvent.isPending}
      />
    </div>
  )
}
